import { i18n } from '@lingui/core';
import { msg, t } from '@lingui/core/macro';
import { z } from 'zod';
import { filterAction } from '@/shared/filters/filter-action';
import { listPageAction } from '@/shared/patterns/list-search';
import { action, can, defineScreen, type Navigation } from '@/shared/screen';
import { AGENT_FORM_KEYS, changedFieldLabels } from './credit-product.agent-fields';
import { creditProductFilters } from './credit-product.filters';
import { CREDIT_PRODUCT_TABS } from './credit-product.search';

export const CREDIT_PRODUCT_PATH = '/cadastro/produtos-credito';
export const creditProductPath = (id: string) =>
  `${CREDIT_PRODUCT_PATH}/${encodeURIComponent(id)}`;

const idInput = z.object({ id: z.string().min(1), name: z.string().optional() });
// B2 (!11105): só as chaves com controle na tela; chave a mais recusa a chamada.
const saveInput = z.object({
  id: z.string().optional(),
  values: z
    .strictObject(Object.fromEntries(AGENT_FORM_KEYS.map((key) => [key, z.unknown().optional()])))
    // eslint-disable-next-line lingui/no-unlocalized-strings -- descrição de ferramenta é inglês canônico
    .describe('Only fields that have a control on the screen, in the form format.'),
});
const cloneInput = idInput.extend({ tenant: z.string().min(1) });
const assignInput = idInput.extend({
  userId: z.string().optional(),
  groupName: z.string().optional(),
  tenant: z.string().optional(),
});
const shareInput = idInput.extend({
  principalType: z.enum(['User', 'Group', 'Tenant']),
  principalId: z.string().min(1),
  permissionLevelType: z.enum(['View', 'Edit']),
});
const exportInput = z.object({ filters: z.record(z.string(), z.string()).optional() });

const loadApi = () => import('./credit-product.api');
const tenantNow = async () => (await import('@/shared/http/client')).getCurrentTenant();

export const creditProductCreate = action({
  id: 'creditProduct.create',
  kind: 'draft',
  label: msg`Novo cadastro`,
  description: msg`Abre o formulário em branco para cadastrar um produto de crédito.`,
  permission: can('CreditProduct', 'Create'),
  run: ({ nav }) => nav.to(creditProductPath('novo')),
});

export const creditProductOpen = action({
  id: 'creditProduct.open',
  kind: 'navigate',
  page: true,
  row: true,
  label: msg`Editar`,
  description: msg`Abre o cadastro de um produto de crédito pelo id, com o formulário e as abas.`,
  input: idInput,
  run: ({ nav, input }) => nav.to(creditProductPath(input.id)),
});

export const creditProductDelete = action({
  id: 'creditProduct.delete',
  kind: 'destructive',
  page: true,
  row: true,
  label: msg`Excluir`,
  description: msg`Exclui um produto de crédito pelo id. Não dá para desfazer. Pede a confirmação da pessoa.`,
  permission: can('CreditProduct', 'Delete'),
  input: idInput,
  preview: (input) => ({
    title: t`Excluir #${input.name ?? input.id}?`,
    detail: t`Tem certeza que deseja excluir este produto de crédito?`,
    tone: 'destructive',
  }),
  run: async ({ input }) => (await loadApi()).deleteCreditProduct(input.id),
});

export const creditProductFilter = filterAction('creditProduct.filter', creditProductFilters, {
  description: msg`Filtra a lista de produtos de crédito: searchString (busca livre), categoryName (categoria), amortizationType (tipo de amortização), name (nome), initialDate (data da ocorrência, AAAA-MM-DD) e termStatus (status de vigência). Filtro omitido é limpo.`,
});
export const creditProductPage = listPageAction('creditProduct.list.page');

/** Nome do Excel quando a API não manda Content-Disposition (`utils/downloadFile.ts:90-99`). */
export function exportFileName(now: Date) {
  const pad = (n: number) => String(n).padStart(2, '0');
  // eslint-disable-next-line lingui/no-unlocalized-strings -- nome-base do arquivo do legado (`creditProductContext.tsx:490`)
  return `ParametrizacoesProdutos_${String(now.getFullYear())}${pad(now.getMonth() + 1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}.xlsx`;
}

export async function runCreditProductExport(
  filters: Record<string, string> = {},
  signal?: AbortSignal,
) {
  const [{ exportCreditProducts }, { creditProductFilters: set }] = await Promise.all([
    loadApi(),
    import('./credit-product.filters'),
  ]);
  return exportCreditProducts(
    set.toParams(filters),
    await tenantNow(),
    exportFileName(new Date()),
    signal,
  );
}

export const creditProductExport = action({
  id: 'creditProduct.export',
  kind: 'read',
  label: msg`Download excel`,
  description: msg`Baixa a planilha Excel com a parametrização dos produtos de crédito, com os filtros da lista. Não muda nada.`,
  permission: can('CreditProduct', 'Read'),
  input: exportInput,
  run: ({ input }) => runCreditProductExport(input.filters),
});

export const creditProductTab = action({
  id: 'creditProduct.tab',
  kind: 'navigate',
  label: msg`Trocar de aba`,
  description: msg`Troca a aba do produto aberto: informacoes, envolvidos, documentos, templates, documentos-para-assinatura, custo-de-emissao ou orgao.`,
  input: z.object({ tab: z.enum(CREDIT_PRODUCT_TABS) }),
  run: ({ nav, input }) => nav.search({ tab: input.tab === 'informacoes' ? undefined : input.tab }),
});

interface RecordCommands {
  readonly refresh?: (() => void) | undefined;
  readonly history?: (() => void) | undefined;
}
let openRecord: RecordCommands = {};
/**
 * O ⌘K roda o manifesto estático com `nav.search` inerte (`shared/commands/model.ts:96-106`): o
 * cadastro aberto liga aqui Atualizar e Histórico (revisão !11105, N1).
 */
export function bindCreditProductRecordCommands(commands: RecordCommands) {
  openRecord = commands;
  return () => {
    if (openRecord === commands) openRecord = {};
  };
}

export const creditProductRefresh = action({
  id: 'creditProduct.refresh',
  kind: 'read',
  label: msg`Atualizar`,
  description: msg`Busca de novo os dados do produto de crédito aberto, sem mudar nada.`,
  run: ({ nav }) => (openRecord.refresh ? openRecord.refresh() : nav.search({})),
});

export const creditProductHistory = action({
  id: 'creditProduct.history',
  kind: 'navigate',
  label: msg`Histórico`,
  description: msg`Abre o painel com o histórico de alterações do produto de crédito.`,
  run: ({ nav }) => (openRecord.history ? openRecord.history() : nav.search({ sheet: 'historico' })),
});

export type CreditProductSaveInput = z.infer<typeof saveInput>;
export interface CreditProductSaveOptions {
  /** Valores do formulário aberto (rascunho); sem ele, o GET atual. */
  readonly current?: () => Record<string, unknown>;
  /** `EXEMPT_WALLET_CATEGORIES` da config da tela; sem a opção, a config de runtime (B1). */
  readonly exemptWalletCategories?: unknown;
}

/** Salvar do agente: os efeitos, as opções e a validação do formulário, sobre o GET atual (B1, B2). */
export async function runCreditProductSave(input: CreditProductSaveInput, options: CreditProductSaveOptions = {}) {
  const [api, form, { prepareAgentSave }] = await Promise.all([
    loadApi(),
    import('./credit-product.form'),
    import('./credit-product.agent-save'),
  ]);
  const exempt =
    'exemptWalletCategories' in options
      ? options.exemptWalletCategories
      : (await import('@/shared/config')).getRuntimeConfig()['EXEMPT_WALLET_CATEGORIES'];
  const tenant = await tenantNow();
  const record = input.id ? await api.getCreditProduct(input.id, tenant) : undefined;
  const initial = options.current
    ? options.current()
    : record
      ? form.creditProductToForm(record)
      : form.emptyCreditProductForm();
  const merged = await prepareAgentSave(i18n, initial, input.values, {
    productId: input.id,
    deps: {
      availableWallets: (params) => api.getAvailableWallets(params),
      settingWallet: async (kind, id) =>
        kind === 'billing'
          ? (await (await import('@/modules/billing-setting/public')).getBillingSetting(id)).walletCode
          : (await (await import('@/modules/communication-setting/public')).getCommunicationSetting(id)).walletCode,
    },
  });
  form.creditProductFormSchema(i18n, { exemptWalletCategories: exempt }).parse(merged);
  return api.saveCreditProduct(
    input.id,
    form.creditProductToWire(merged, input.id ? 'edit' : 'create', record),
    tenant,
  );
}

/** Prévia do Salvar com os campos que mudam (B2); sem o formulário aberto, os campos enviados. */
export function creditProductSavePreview(input: CreditProductSaveInput, current?: Record<string, unknown>) {
  const values = input.values;
  const name =
    typeof values['name'] === 'string'
      ? values['name']
      : typeof current?.['name'] === 'string'
        ? current['name']
        : (input.id ?? '');
  const fields = changedFieldLabels(i18n, values, current).join(', ');
  return {
    title: t`Salvar produto de crédito`,
    detail: fields
      ? t`Salvar ${name}. Campos alterados: ${fields}.`
      : t`Salvar ${name} sem alterar campos.`,
  };
}

export const creditProductSave = action({
  id: 'creditProduct.save',
  kind: 'write',
  permission: can('CreditProduct', 'Update'),
  label: msg`Salvar`,
  description: msg`Salva o produto de crédito com as mesmas validações do formulário. Recebe só os campos que têm controle na tela. Pede a confirmação da pessoa, com os campos alterados.`,
  input: saveInput,
  preview: (input) => creditProductSavePreview(input),
  run: ({ input }) => runCreditProductSave(input),
});

export async function runCreditProductClone(input: z.infer<typeof cloneInput>, nav: Navigation) {
  const api = await loadApi();
  const form = await import('./credit-product.form');
  const { cloneSchema } = await import('./credit-product.record-rules');
  const record = await api.getCreditProduct(input.id, await tenantNow());
  // As regras do diálogo (`CloneProductSchema.ts:7-24`): nome novo diferente do atual.
  cloneSchema(i18n, record.name).parse({
    tenant: input.tenant,
    keepSameName: input.name === undefined,
    name: input.name ?? '',
  });
  const result = await api.cloneCreditProduct(
    form.clonePayload(record, input.name ?? record.name),
    input.tenant,
  );
  await nav.to(creditProductPath(typeof result === 'string' ? result : result.id));
  return result;
}

export const creditProductClone = action({
  id: 'creditProduct.clone',
  kind: 'write',
  label: msg`Clonar produto`,
  description: msg`Copia o produto de crédito para o correspondente escolhido (tenant), com o mesmo nome ou com um nome novo, e abre a cópia. Pede a confirmação da pessoa.`,
  permission: can('CreditProduct', 'Update'),
  input: cloneInput,
  preview: (input) => ({
    title: t`Clonar produto`,
    detail: t`Criar uma cópia no correspondente ${input.tenant}.`,
  }),
  run: ({ input, nav }) => runCreditProductClone(input, nav),
});

/** Atribuir do agente com a regra do diálogo: operador ou grupo (`AssignSchema.ts`, B2). */
export async function runCreditProductAssign(input: z.infer<typeof assignInput>) {
  const { assignSchema } = await import('./credit-product.record-rules');
  assignSchema(i18n).parse({
    tenant: input.tenant ?? '',
    userId: input.userId || null,
    userIdDisplay: null,
    groupName: input.groupName || null,
    groupNameDisplay: null,
  });
  return (await loadApi()).assignCreditProduct(input.id, input);
}

export const creditProductAssign = action({
  id: 'creditProduct.assign',
  kind: 'write',
  label: msg`Atribuir`,
  description: msg`Atribui o produto de crédito a um operador (userId) ou a um grupo (groupName) de um correspondente (tenant). Pede a confirmação da pessoa.`,
  permission: can('CreditProduct', 'Update'),
  input: assignInput,
  preview: (input) => ({
    title: t`Atribuir`,
    detail: t`Atribuir o registro a ${input.userId || input.groupName || input.tenant || ''}.`,
  }),
  run: ({ input }) => runCreditProductAssign(input),
});

export const creditProductShare = action({
  id: 'creditProduct.share',
  kind: 'write',
  label: msg`Compartilhar registro`,
  description: msg`Compartilha o produto de crédito com um usuário, grupo ou correspondente, com permissão de ver ou de editar. Pede a confirmação da pessoa.`,
  permission: can('CreditProduct', 'Update'),
  input: shareInput,
  preview: (input) => ({
    title: t`Compartilhar registro`,
    detail: t`Compartilhar com ${input.principalId} (${input.permissionLevelType}).`,
  }),
  run: async ({ input }) =>
    (await loadApi()).shareCreditProduct(input.id, input, await tenantNow()),
});

const access = { flag: { key: 'CREDIT_PRODUCT_MENU', notEquals: 0 } } as const;

export const creditProductListScreen = defineScreen({
  id: 'creditProduct.list',
  path: CREDIT_PRODUCT_PATH,
  title: msg`Produtos de Crédito`,
  description: msg`Lista de produtos de crédito: busca, filtros, paginação, planilha Excel da parametrização, abrir e excluir.`,
  permission: can('CreditProduct', 'Read'),
  access,
  place: { icon: 'receipt-text', section: 'register' },
  actions: [
    creditProductCreate,
    creditProductOpen,
    creditProductDelete,
    creditProductFilter,
    creditProductPage,
    creditProductExport,
  ],
});

export const creditProductDetailScreen = defineScreen({
  id: 'creditProduct.detail',
  path: `${CREDIT_PRODUCT_PATH}/$productId`,
  title: msg`Produto de crédito`,
  description: msg`Cadastro de um produto de crédito (novo ou edição): abas de informações, envolvidos, documentos obrigatórios, templates, documentos para assinatura, custo de emissão e órgão; salvar, atualizar, atribuir, clonar, compartilhar, excluir e histórico.`,
  permission: can('CreditProduct', 'Read'),
  access,
  place: {
    icon: 'file-text',
    parent: 'creditProduct.list',
    record: msg`Produto de crédito`,
    tabs: [
      { id: 'informacoes', label: msg`Informações` },
      { id: 'envolvidos', label: msg`Envolvidos` },
      { id: 'documentos', label: msg`Documentos obrigatórios` },
      { id: 'templates', label: msg`Templates` },
      { id: 'documentos-para-assinatura', label: msg`Documentos para Assinatura` },
      { id: 'custo-de-emissao', label: msg`Custo de Emissão` },
      { id: 'orgao', label: msg`Orgão` },
    ],
  },
  actions: [
    creditProductSave,
    creditProductRefresh,
    creditProductAssign,
    creditProductClone,
    creditProductShare,
    creditProductDelete,
    creditProductHistory,
    creditProductTab,
  ],
});
